import React, { useState, useEffect } from 'react';
import { ProductService, OptimizationService, NodeService } from '../../services/api';
import { Product, AllocationStrategy, OptimizationRequest, OptimizationResult, OptimizationStatus, SolverInfo } from '../../types';
import PurchaseOrderForm from './PurchaseOrderForm';
import OptimizationResults from './OptimizationResults';
import './OptimizationEngine.css';

interface OptimizationEngineProps {}

const OptimizationEngine: React.FC<OptimizationEngineProps> = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [nodeNameMap, setNodeNameMap] = useState<Map<string, string>>(new Map());
  const [solverInfo, setSolverInfo] = useState<SolverInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [optimizationResult, setOptimizationResult] = useState<OptimizationResult | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>('');

  useEffect(() => {
    loadInitialData();
  }, []);

  // Make optimization data available globally for AI Assistant context
  useEffect(() => {
    const optimizationData = {
      products,
      solver_info: solverInfo,
      recent_results: optimizationResult ? [optimizationResult] : [],
      node_name_map: Object.fromEntries(nodeNameMap),
      is_loading: isLoading,
      error: error
    };
    (window as any).warehouseOptimizationData = optimizationData;
  }, [products, solverInfo, optimizationResult, nodeNameMap, isLoading, error]);

  const loadInitialData = async () => {
    try {
      // Load products and nodes - these are essential for the dropdown
      const [productsData, nodesData] = await Promise.all([
        ProductService.getProducts(),
        NodeService.getNodes()
      ]);
      
      setProducts(productsData);
      
      // Create node name mapping
      const nameMap = new Map<string, string>();
      nodesData.forEach(node => {
        nameMap.set(node.node_id, node.name);
      });
      setNodeNameMap(nameMap);

      // Try to load solver info, but don't fail if it's not available
      try {
        const solverData = await OptimizationService.getSolverInfo();
        setSolverInfo(solverData);
      } catch (solverErr) {
        console.warn('Solver info not available, using defaults:', solverErr);
        // Set default solver info so the form still works
        setSolverInfo({
          simple_available: true,
          advanced_available: false,
          default_solver: 'simple',
          simple_features: ['Basic allocation', 'Fast computation'],
          advanced_features: []
        });
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load products and nodes. Please try again.');
    }
  };

  const handleOptimizationSubmit = async (
    productId: string,
    quantity: number,
    strategy: AllocationStrategy,
    solverType: 'simple' | 'advanced'
  ) => {
    setIsLoading(true);
    setError(null);
    setOptimizationResult(null);
    setLoadingMessage('Preparing optimization...');

    try {
      const optimizationRequest: OptimizationRequest = {
        product_id: productId,
        purchase_quantity: quantity,
        allocation_strategy: strategy,
        solver_type: solverType
      };

      setLoadingMessage(`Running ${solverType} optimization...`);
      
      const response = await OptimizationService.runOptimization(optimizationRequest);
      
      if (response.result.status === OptimizationStatus.OPTIMAL) {
        setOptimizationResult(response.result);
        setLoadingMessage('');
      } else if (response.result.status === OptimizationStatus.INFEASIBLE) {
        setError('Optimization problem is infeasible. This may occur when there are insufficient warehouse capacities or conflicting constraints.');
      } else if (response.result.status === OptimizationStatus.TIMEOUT) {
        setError('Optimization timed out. Please try with a smaller problem size or simpler constraints.');
      } else {
        setError(`Optimization failed with status: ${response.result.status}`);
      }
    } catch (err: any) {
      console.error('Optimization error:', err);
      setError(err.message || 'Failed to run optimization');
      setLoadingMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearResults = () => {
    setOptimizationResult(null);
    setError(null);
  };

  return (
    <div className="optimization-engine">
      <div className="optimization-header">
        <h2>Purchase Order Optimization</h2>
        <p>
          Use linear programming to optimize the allocation of incoming purchase orders 
          across your warehouse network to minimize demand gaps.
        </p>
      </div>

      {error && (
        <div className="error-message">
          <h4>Optimization Error</h4>
          <p>{error}</p>
          <button 
            className="btn btn-secondary"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="optimization-content">
        <div className="optimization-input">
          <PurchaseOrderForm
            products={products}
            solverInfo={solverInfo}
            onSubmit={handleOptimizationSubmit}
            isLoading={isLoading}
            disabled={isLoading}
          />
        </div>

        {isLoading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <h3>Optimizing Allocation...</h3>
            <p>{loadingMessage}</p>
            <div className="loading-details">
              <p>This may take a few moments depending on the complexity of your warehouse network.</p>
            </div>
          </div>
        )}

        {optimizationResult && !isLoading && (
          <div className="optimization-output">
            <OptimizationResults
              result={optimizationResult}
              nodeNameMap={nodeNameMap}
              onClear={handleClearResults}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OptimizationEngine;